<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { Container, Icon, Text, toast } from '@Shelly/shelly-design-system';

	import {
		accountHasCameraDevice,
		CAMERA_CLOUD_STORAGE_PLANS,
		getPlanByTier,
		subscribeCameraCloudStorage,
		upgradeCameraCloudStorage,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { convertDatePayment } from 'src/helpers/date_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		CameraCloudStorageSubscriptionStatus,
		type CameraCloudStorageActivePlan,
	} from 'src/types/CameraCloudStorage';

	import NoCameraDeviceModal from './NoCameraDeviceModal.svelte';

	export let activePlan: CameraCloudStorageActivePlan;

	const dispatch = createEventDispatcher<{ selectPlan: void }>();

	let actionLoading = false;
	let showNoCameraDeviceModal = false;

	$: plan = getPlanByTier(activePlan.tier);
	$: priceMonthly = plan?.priceMonthly;
	/** All plans that can be upgraded to from the active tier (BE-924 §3 — no downgrade). */
	$: upgradeTiers = CAMERA_CLOUD_STORAGE_PLANS.filter((p) => p.tier > activePlan.tier).map(
		(p) => p.tier,
	);
	/**
	 * When more than one bigger plan exists (e.g. on tier 3 the user can pick
	 * between 6 or 10 cameras) we cannot silently default to the next tier —
	 * FE-1719. The card emits `selectPlan` so the parent can navigate the
	 * user to the plans grid where they pick the target plan explicitly.
	 */
	$: requiresPlanSelection = upgradeTiers.length > 1;
	$: nextTier = upgradeTiers[0];
	$: canUpgrade =
		activePlan.status === CameraCloudStorageSubscriptionStatus.Active && upgradeTiers.length > 0;
	$: canRenew = activePlan.status === CameraCloudStorageSubscriptionStatus.Cancelled;
	$: isUpcomingPayment = activePlan.status === CameraCloudStorageSubscriptionStatus.Active;

	async function handleUpgrade() {
		if (actionLoading) return;
		if (requiresPlanSelection) {
			dispatch('selectPlan');
			return;
		}
		if (!nextTier) return;
		if (!accountHasCameraDevice()) {
			showNoCameraDeviceModal = true;
			return;
		}
		actionLoading = true;
		try {
			const result = await upgradeCameraCloudStorage(nextTier);
			if (result.error) {
				toast.open({
					type: 'danger',
					title: $format(result.error.key, {
						default: result.error.default,
					}),
				});
			}
		} finally {
			actionLoading = false;
		}
	}

	async function handleRenew() {
		if (actionLoading) return;
		if (!accountHasCameraDevice()) {
			showNoCameraDeviceModal = true;
			return;
		}
		actionLoading = true;
		try {
			const result = await subscribeCameraCloudStorage(activePlan.tier);
			if (result.error) {
				toast.open({
					type: 'danger',
					title: $format(result.error.key, {
						default: result.error.default,
					}),
				});
			}
		} finally {
			actionLoading = false;
		}
	}
</script>

<Container padding="0" type="modalContainer">
	<div class="mt-m mb-m ml-m mr-m">
		<!-- Row 1: icon + title/price (left) | status (right) -->
		<div class="d-flex justify-content-between align-items-center gap-s">
			<div class="d-flex align-items-center gap-s ccs-plan-info-title">
				<Icon size="s" icon="fas fa-camera" class="text-white" />
				<div>
					<Text styleType="body-small" class="text-bold" noWrap>
						{$format('label_camera_cloud_storage_plan_card_title', {
							default: `${activePlan.tier} Cameras Cloud Storage`,
							values: { count: activePlan.tier },
						})}
					</Text>
					{#if priceMonthly !== undefined}
						<Text styleType="body-small" noWrap>
							{$format('label_price_month', {
								default: '€{price}/month',
								values: { price: priceMonthly },
							})}
						</Text>
					{/if}
				</div>
			</div>
			<div class="d-flex align-items-center gap-xs flex-shrink-0">
				{#if activePlan.status === CameraCloudStorageSubscriptionStatus.Active}
					<Text class="text-success text-bold" styleType="body-small" noWrap>
						{capFirstChar($format('active', { default: 'Active' }))}
					</Text>
					<Icon size="s" icon="fas fa-circle-check text-success" />
				{:else if activePlan.status === CameraCloudStorageSubscriptionStatus.Cancelled}
					<Text class="text-danger text-bold" styleType="body-small" noWrap>
						{$format('label_cancelled', { default: 'Cancelled' })}
					</Text>
					<Icon size="s" icon="fas fa-circle-xmark text-danger" />
				{:else if activePlan.status === CameraCloudStorageSubscriptionStatus.Suspended}
					<Text class="text-bold" styleType="body-small" noWrap>
						{$format('label_paused', { default: 'Paused' })}
					</Text>
					<Icon size="s" icon="fas fa-circle-pause" />
				{/if}
			</div>
		</div>

		<!-- Row 3: actions (left) | payment heading + date (right) -->
		<div class="d-flex justify-content-between align-items-center mt-s">
			<div>
				{#if canUpgrade}
					<Text
						styleType="body-small"
						class="link-styles text-primary"
						on:click={handleUpgrade}
						noWrap
					>
						{$format('upgrade_now_text', { default: 'Upgrade now' })}
					</Text>
				{:else if canRenew}
					<Text
						styleType="body-small"
						class="link-styles text-primary"
						on:click={handleRenew}
						noWrap
					>
						{$format('renew_now_text', { default: 'Renew now' })}
					</Text>
				{/if}
			</div>
			{#if activePlan.nextBillingTime}
				<div class="text-right flex-shrink-0">
					<Text
						styleType="body-small"
						class="text-uppercase text-color-muted text-bold"
						noWrap
					>
						{#if isUpcomingPayment}
							{$format('upcoming_payment_heading', { default: 'Upcoming Payment' })}
						{:else}
							{$format('active_untill_heading', { default: 'Active until' })}
						{/if}
					</Text>
					<Text styleType="body-small" noWrap>
						{convertDatePayment(activePlan.nextBillingTime)}
					</Text>
				</div>
			{/if}
		</div>
	</div>
</Container>

{#if showNoCameraDeviceModal}
	<NoCameraDeviceModal
		bind:showModal={showNoCameraDeviceModal}
		on:close={() => (showNoCameraDeviceModal = false)}
	/>
{/if}

<style>
	.ccs-plan-info-title {
		min-width: 0;
	}
</style>
